
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { UserAvatar } from "@/components/UserAvatar";
import { StarRating } from "@/components/StarRating";
import { formatDistanceToNow } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";

export interface Review {
  id: string;
  rating: number;
  comment: string;
  reviewer: {
    id: string;
    name: string;
    avatarUrl?: string;
  };
  date: Date;
  serviceTitle?: string;
  verified: boolean;
}

interface RecentReviewsProps {
  reviews: Review[];
  maxItems?: number;
  showViewAll?: boolean;
  viewAllLink?: string;
}

export function RecentReviews({
  reviews,
  maxItems = 3,
  showViewAll = true,
  viewAllLink = "/reviews",
}: RecentReviewsProps) {
  const displayedReviews = reviews.slice(0, maxItems);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between">
          <span>Recent Reviews</span>
          {showViewAll && reviews.length > maxItems && (
            <Link to={viewAllLink} className="text-sm font-normal text-anyskill-blue hover:underline">
              View All
            </Link>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {displayedReviews.length > 0 ? (
          displayedReviews.map((review) => (
            <div key={review.id} className="border-b pb-4 last:border-0 last:pb-0">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-3">
                  <UserAvatar
                    userId={review.reviewer.id}
                    avatarUrl={review.reviewer.avatarUrl}
                    fallback={review.reviewer.name.charAt(0)}
                    size="sm"
                  />
                  <div>
                    <div className="font-medium">{review.reviewer.name}</div>
                    <div className="text-xs text-gray-500">
                      {formatDistanceToNow(review.date, { addSuffix: true })}
                    </div>
                  </div>
                </div>
                <StarRating value={review.rating} readOnly />
              </div>
              
              {review.serviceTitle && (
                <div className="mb-2 text-sm">
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                    {review.serviceTitle}
                  </Badge>
                  {review.verified && (
                    <Badge variant="outline" className="ml-2 bg-green-50 text-green-700 border-green-200">
                      Verified
                    </Badge>
                  )}
                </div>
              )}
              
              <p className="text-sm text-gray-700">{review.comment}</p>
            </div>
          ))
        ) : (
          <div className="text-center py-6">
            <p className="text-gray-500">No reviews yet</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
